import { GearItem, GearTarget, Rarity, primaryValue } from "@highmarch/domain";
import { FusionCandidates, FusionPreview, fuseItems, fusionCandidates, itemScore } from "@highmarch/app";
import { audio } from "../../audio/audio";
import { RARITY_LABEL, fmtStat, pct, pctStep } from "../gearUi";
import { motionOk } from "../kit";
import { toast } from "../toast";
import { guide } from "../guide/guide";
import { SIGIL_RARITIES } from "../forgeArt";
import type { ArmouryCtx } from "./host";
import { holdButton } from "./hold";
import { btn, essImg, essenceEl, gearDisc, h, ico, refusal, sigilImg, statLabel, statShort } from "./itemBits";
import { closeUndo } from "./itemActions";
import { burst, celebrateMythic, shine } from "./itemFx";
import { closeButton, mountPanel, onRefresh, refreshPanels } from "./itemPanels";
import {
  FusionDraft, draftFull, draftPreview, draftRequest, essenceAfter, fuseBadge, groupKeepers, initialDraft, kindWord, toggleFodder, withSigils,
} from "./forgeModel";
import { gearTile } from "./tile";

/**
 * The Fusion screen (gear v2, docs/GEAR_V2.md §3): three items of one rarity and kind become one of the next rarity.
 * Two steps on one panel: pick the KEEPER (the stash's fusable groups — the FUSE badge — each listing its items, worn
 * ones first) and then the plan — the two spares the player would least miss already picked, Fusion Sigils standing
 * in for what is short, the fee, and the EXACT result (the same request always rolls the same item): its level, stats,
 * affixes (new ones marked), and for Legendary → Mythic the effect the player chooses. Hold to fuse; the three discs
 * fly together, a flash in the new rarity's colour, and the result rises.
 */

/** How long the hold to fuse lasts (a deliberate press: two items go for good). */
export const FUSE_HOLD_MS = 1200;
/** The fusion animation: the discs converge, the flash, the result's rise. */
const CONVERGE_MS = 520;
const FLASH_MS = 380;
const RISE_MS = 520;

export interface FusionOptions {
  /** Open straight on this keeper's plan (the item card's FUSE). */
  readonly keeper?: GearItem;
  /** After the fusion: show the result's item card. */
  readonly openItem?: (item: GearItem) => void;
}

export function openFusion(ctx: ArmouryCtx, opts: FusionOptions = {}): void {
  const profile = ctx.profile, B = ctx.B;
  const panel = mountPanel(ctx, "afuse");
  const root = panel.root;
  let keeper: number | null = opts.keeper?.id ?? null;
  let draft: FusionDraft | null = null;
  let done: GearItem | null = null;
  let busy = false;
  /** Opened on a keeper: Back returns to the item card (close), not to the picker. */
  const direct = opts.keeper !== undefined;

  const head = (title: string, sub: string, back: (() => void) | null) => {
    const b = back ? btn("icbtn aback", ico("back", 18)) : null;
    if (b && back) { b.setAttribute("aria-label", "Back"); b.addEventListener("click", back); }
    return h("div", "fhead", b, h("div", "tt", h("b", "disp", title), h("small", "", sub)), closeButton(panel.close));
  };

  /** Essence and the sigils held: the screen's purse. */
  const purse = (): HTMLElement => {
    const row = h("div", "fpurse", h("span", "pe", essImg(), h("b", "", String(profile.forge.essence)), h("small", "", "essence")));
    for (const r of SIGIL_RARITIES) {
      const n = profile.forge.sigils[r] ?? 0;
      const chip = h("span", `ps r${r}${n === 0 ? " none" : ""}`, sigilImg(r), h("b", "", String(n)));
      chip.title = `${RARITY_LABEL[r]} sigils: ${n}`;
      row.append(chip);
    }
    return row;
  };

  const render = () => {
    if (done !== null) { renderDone(done); return; }
    const k = keeper === null ? null : profile.findGear(keeper);
    if (k === null || k === undefined) { keeper = null; draft = null; renderPick(); return; }
    renderPlan(k);
  };

  // ------------------------------------------------------------------ step 1: the keeper
  function renderPick(): void {
    root.className = "apanel afuse pick";
    // The rarest fusions first (the ones that matter most), and the keeper rule said once.
    const groups = [...fuseBadge(profile).groups].sort((a, b) => b.rarity - a.rarity || a.kind - b.kind);
    const list = h("div", "flist");
    if (groups.length === 0) {
      list.append(h("div", "fnone", h("span", "rings", sigilImg(Rarity.Rare, "big")), h("b", "disp", "Nothing to fuse yet"), h("p", "", "3 of a rarity forge 1 of the next")));
    }
    for (const g of groups) {
      const items = groupKeepers(profile, g);
      const next = (g.rarity + 1) as Rarity;
      const sec = h("section", `fgroup r${g.rarity}`,
        h("div", "gh", h("i", `rdot r${g.rarity}`), h("span", "", `3 ${RARITY_LABEL[g.rarity]} ${g.kind === GearTarget.Base ? "fortress" : "tower"} → 1 ${RARITY_LABEL[next]}`), h("span", "ln"),
          h("span", "note", `${items.length} item${items.length === 1 ? "" : "s"}${g.sigils > 0 ? ` · ${g.sigils} sigil${g.sigils === 1 ? "" : "s"}` : ""}`)));
      const grid = h("div", "sgrid");
      for (const it of items) {
        const t = gearTile(ctx, it, { wornChip: true, size: "m" });
        t.addEventListener("click", () => { keeper = it.id; draft = null; render(); });
        grid.append(h("div", "stile", t));
      }
      sec.append(grid);
      list.append(sec);
    }
    root.replaceChildren(h("div", "grab"), head("Fusion", "Pick the keeper", null), purse(), list);
  }

  // ------------------------------------------------------------------ step 2: the plan
  function renderPlan(k: GearItem): void {
    root.className = `apanel afuse plan r${k.rarity}`;
    const c: FusionCandidates = fusionCandidates(profile, k);
    if (draft === null || draft.keeper !== k.id) {
      draft = initialDraft(c);
      const here = k.equippedTo !== null && k.equippedTo.target === GearTarget.Tower ? B.towers[k.equippedTo.towerIndex]?.id : undefined;
      const fit = c.mythicChoices.find((m) => m.towerId === here);
      if (fit) draft = { ...draft, mythic: fit.index };
    }
    const d = draft;
    const next = (k.rarity + 1) as Rarity;
    const preview = draftPreview(profile, d);
    const p = typeof preview === "object" && preview !== null ? preview : null;

    // The stage: keeper + two slots → the result.
    const slot = (i: number): HTMLElement => {
      const id = d.fodder[i];
      if (id !== undefined) {
        const it = profile.findGear(id);
        const s = h("div", "fslot item", it ? gearDisc(B, it, "adisc sm") : null, h("small", "", "spare"));
        s.dataset.k = `f${i}`;
        return s;
      }
      if (i - d.fodder.length < d.sigils) {
        const s = h("div", "fslot sigil", h("span", "sigw", sigilImg(k.rarity, "big")), h("small", "", "sigil"));
        s.dataset.k = `f${i}`;
        return s;
      }
      return h("div", "fslot empty", h("span", "plus", "+"), h("small", "", "pick a spare"));
    };
    const keep = h("div", "fslot keeper", gearDisc(B, k, "adisc sm"), h("small", "", "keeper"));
    keep.dataset.k = "keeper";
    const result = p !== null ? gearDisc(B, p.plan.result, "adisc res") : h("div", "adisc res ghost", sigilImg(next, "big"));
    const resultWrap = h("div", `fresult r${next}`, result, h("small", "disp", RARITY_LABEL[next].toUpperCase()));
    const stage = h("div", "fstage", h("div", "three", keep, h("span", "op", "+"), slot(0), h("span", "op", "+"), slot(1)), h("span", "arrow", ico("chev", 22)), resultWrap);

    const sections: (HTMLElement | null)[] = [stage, resultCard(k, p, preview)];
    if (c.mythicChoices.length > 0) sections.push(mythicPick(c, d));
    sections.push(sigilRow(c, d), spares(c, d), costRow(c, p));

    const go = fuseButton(k, p);
    root.replaceChildren(h("div", "grab"), head(`Fuse into ${RARITY_LABEL[next]}`, `3 ${RARITY_LABEL[k.rarity]} ${kindWord(B, k)} gear → 1 ${RARITY_LABEL[next]}`,
      direct ? null : () => { keeper = null; draft = null; render(); }), h("div", "fbody", ...sections), h("div", "ffoot", go.el));
  }

  function resultCard(k: GearItem, p: FusionPreview | null, preview: FusionPreview | string | null): HTMLElement {
    if (p === null) {
      const why = typeof preview === "string" ? refusal(preview) : "Add two spares or sigils";
      return h("div", "fcard wait", h("p", "", why));
    }
    const r = p.plan.result;
    const newAffixes = r.affixes.slice(k.affixes.length), [primNow, primNext] = pctStep(p.primaryBefore, p.primaryAfter);
    const lines: HTMLElement[] = [
      h("div", "fl big", h("b", "disp", `${RARITY_LABEL[r.rarity]} ${B.gear.slots[r.slot].name} Lv ${r.level}`)),
      h("div", "fl", h("span", "k", statLabel(r.primaryStat)), h("span", "v", primNow, " → ", h("em", "", primNext))),
      h("div", "fl", h("span", "k", "Gear power"), h("span", "v", `⚡${p.powerBefore} → `, h("em", "", `⚡${p.powerAfter}`))),
    ];
    if (r.level > k.level) lines.push(h("div", "fl", h("span", "k", "Item level"), h("span", "v", `Lv ${k.level} → `, h("em", "", `Lv ${r.level}`), h("small", "", " · best of the three"))));
    const chips = h("div", "affx");
    for (const a of r.affixes) {
      const isNew = newAffixes.includes(a);
      chips.append(h("span", `chip${isNew ? " new" : ""}`, isNew ? h("i", "nw", "NEW") : null, `${pct(a.value)} ${fmtStat(a.stat)}`));
    }
    if (r.affixes.length > 0) lines.push(chips);
    if (r.mythicEffect >= 0) {
      const m = B.gear.mythicEffects[r.mythicEffect];
      if (m) lines.push(h("div", "amyth on", ico("star", 13), h("span", "t", h("b", "", m.name), ` ${m.description}`)));
    }
    const keeps = [k.equippedTo !== null ? "stays worn" : "", k.locked ? "stays locked" : "", r.bound ? "bound" : ""].filter(Boolean);
    if (keeps.length > 0) lines.push(h("small", "fkeep", keeps.join(" · ")));
    return h("div", `fcard r${r.rarity}`, ...lines);
  }

  function mythicPick(c: FusionCandidates, d: FusionDraft): HTMLElement {
    const box = h("div", "fmyth", h("div", "gh", ico("star", 12), h("span", "", "Choose its Mythic effect"), h("span", "ln")));
    // The effect that acts where the keeper is worn comes first, tagged (a Mythic effect does nothing on another tower).
    const here = c.keeper.equippedTo !== null && c.keeper.equippedTo.target === GearTarget.Tower ? B.towers[c.keeper.equippedTo.towerIndex]?.id ?? null : null;
    const choices = [...c.mythicChoices].sort((a, b) => Number(b.towerId === here) - Number(a.towerId === here));
    for (const m of choices) {
      const on = d.mythic === m.index;
      const tag = here !== null && m.towerId === here ? h("i", "here", `acts on ${B.towers[c.keeper.equippedTo!.towerIndex].name}`) : null;
      const row = btn(`fmrow${on ? " on" : ""}`, h("span", "rad"), h("span", "t", h("b", "", m.name, tag), h("small", "", m.description)));
      row.setAttribute("role", "radio");
      row.setAttribute("aria-checked", String(on));
      row.addEventListener("click", () => { draft = { ...d, mythic: m.index }; render(); });
      box.append(row);
    }
    return box;
  }

  function sigilRow(c: FusionCandidates, d: FusionDraft): HTMLElement {
    const k = c.keeper;
    const minus = btn("icbtn sm sminus", "−"), plus = btn("icbtn sm splus", "+");
    minus.setAttribute("aria-label", "One sigil fewer"); plus.setAttribute("aria-label", "One sigil more");
    const can = Math.min(c.sigilsHeld, c.maxSigils, 2);
    if (d.sigils <= 0) minus.disabled = true;
    if (d.sigils >= can) plus.disabled = true;
    minus.addEventListener("click", () => { draft = withSigils(d, d.sigils - 1, c); render(); });
    plus.addEventListener("click", () => { draft = withSigils(d, d.sigils + 1, c); render(); });
    const note = `${c.sigilsHeld} held`;
    return h("div", `fsig${c.sigilsHeld === 0 ? " none" : ""}`, sigilImg(k.rarity), h("div", "mid", h("b", "", `${RARITY_LABEL[k.rarity]} sigils`), h("small", "", note)),
      h("div", "step", minus, h("b", "n disp", String(d.sigils)), plus));
  }

  function spares(c: FusionCandidates, d: FusionDraft): HTMLElement {
    const sec = h("div", "fspares", h("div", "gh", h("span", "", "Spares"), h("span", "ln"), h("span", "note", "")));
    if (c.fodder.length === 0) { sec.append(h("p", "snote", `No spare ${RARITY_LABEL[c.keeper.rarity]} ${kindWord(B, c.keeper)} items`)); return sec; }
    const grid = h("div", "sgrid");
    for (const it of c.fodder) {
      const on = d.fodder.includes(it.id);
      const t = gearTile(ctx, it, { size: "m" });
      const cell = h("div", `jpick${on ? " picked" : ""}`, t, on ? h("span", "chk", "✓") : null);
      cell.setAttribute("role", "checkbox");
      cell.setAttribute("aria-checked", String(on));
      cell.addEventListener("click", () => { draft = toggleFodder(d, it.id); if (draft.fodder.length + draft.sigils > 2) draft = { ...draft, sigils: 2 - draft.fodder.length }; render(); });
      grid.append(cell);
    }
    sec.append(grid);
    return sec;
  }

  function costRow(c: FusionCandidates, p: FusionPreview | null): HTMLElement {
    const fee = p?.plan.fee ?? c.fee, back = p?.plan.essenceBack ?? 0;
    const left = p !== null ? essenceAfter(profile, p) : profile.forge.essence - fee;
    const row = h("div", `fcost${left < 0 ? " short" : ""}`, h("span", "k", "Forge fee"), essenceEl(fee, profile.forge.essence + back));
    if (back > 0) row.append(h("small", "back", `+${back} back from the spares' raises`));
    row.append(h("small", "left", left < 0 ? `need ${-left} more` : `${left} left`));
    return row;
  }

  function fuseButton(k: GearItem, p: FusionPreview | null) {
    const ok = p !== null && essenceAfter(profile, p) >= 0;
    return holdButton([h("span", "disp", "FUSE"), h("small", "", p === null ? "pick three" : ok ? "hold to forge" : "not enough essence")], {
      ms: FUSE_HOLD_MS, className: "abtn gold fusebig", disabled: !ok, label: `Hold to fuse into ${RARITY_LABEL[k.rarity + 1]}`,
      onDone: () => fuseNow(),
      onTap: () => { if (ok) toast(ctx.toastParent, "Hold the button to fuse"); },
    });
  }

  function fuseNow(): void {
    if (busy || draft === null || !draftFull(draft)) return;
    const plan = fuseItems(profile, draftRequest(draft));
    if (typeof plan === "string") { toast(ctx.toastParent, refusal(plan)); render(); return; }
    busy = true;
    closeUndo();
    audio.play("crateOpen");
    ctx.changed();
    refreshPanels(ctx, root);
    guide()?.signal("gearFused");
    const result = plan.result;
    const finish = () => {
      busy = false;
      done = profile.findGear(result.id) ?? result;
      audio.play("towerUpgradeMax");
      render();
      const disc = root.querySelector<HTMLElement>(".fdone .adisc");
      if (disc) { burst(disc, result.rarity); shine(disc); }
      if (result.mythicEffect >= 0 && result.equippedTo !== null) celebrateMythic(ctx, result, result.equippedTo);
    };
    converge(finish);
  }

  /** The three slots fly into the result's place; a flash in the new rarity's colour; then `then`. */
  function converge(then: () => void): void {
    const target = root.querySelector<HTMLElement>(".fresult");
    const slots = Array.from(root.querySelectorAll<HTMLElement>(".fstage .fslot"));
    if (!motionOk() || target === null || typeof target.getBoundingClientRect !== "function" || slots.some((s) => typeof s.animate !== "function")) { then(); return; }
    const t = target.getBoundingClientRect();
    for (const [i, s] of slots.entries()) {
      const r = s.getBoundingClientRect();
      const dx = t.left + t.width / 2 - (r.left + r.width / 2), dy = t.top + t.height / 2 - (r.top + r.height / 2);
      s.animate([{ transform: "translate(0,0) rotate(0) scale(1)", opacity: 1 }, { transform: `translate(${dx * 0.35}px, ${dy - 28}px) rotate(${(i - 1) * 25}deg) scale(1.05)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) rotate(${(i - 1) * 90}deg) scale(0.25)`, opacity: 0.2 }], { duration: CONVERGE_MS, delay: i * 40, easing: "cubic-bezier(.5,0,.7,.4)", fill: "forwards" });
    }
    const flash = h("span", `fflash r${(Number(root.className.match(/r(\d)/)?.[1] ?? 0) + 1)}`);
    target.append(flash);
    flash.animate([{ transform: "translate(-50%,-50%) scale(0.2)", opacity: 0 }, { transform: "translate(-50%,-50%) scale(1)", opacity: 1, offset: 0.4 }, { transform: "translate(-50%,-50%) scale(1.8)", opacity: 0 }],
      { duration: FLASH_MS, delay: CONVERGE_MS - 80, easing: "ease-out", fill: "backwards" }).onfinish = () => { flash.remove(); then(); };
  }

  // ------------------------------------------------------------------ step 3: forged
  function renderDone(it: GearItem): void {
    root.className = `apanel afuse done r${it.rarity}`;
    const view = h("div", "fdone", h("small", "kick disp", `${RARITY_LABEL[it.rarity].toUpperCase()} FORGED!`), gearDisc(B, it, "adisc res"),
      h("b", "disp nm", `${RARITY_LABEL[it.rarity]} ${B.gear.slots[it.slot].name} Lv ${it.level}`),
      h("span", "pw", `⚡${itemScore(B, it)} · ${pct(primaryValue(B, it))} ${statShort(it.primaryStat)}`));
    const again = fuseBadge(profile).count > 0 ? btn("abtn parch", h("span", "disp", "Fuse more")) : null;
    again?.addEventListener("click", () => { done = null; keeper = null; draft = null; render(); });
    const see = btn("abtn gold", h("span", "disp", "See item"));
    see.addEventListener("click", () => { panel.close(); opts.openItem?.(it); });
    root.replaceChildren(h("div", "grab"), head("Fusion", "Three became one", null), view, h("div", "ffoot two", again, see));
    if (motionOk()) {
      const d = view.querySelector<HTMLElement>(".adisc");
      d?.animate?.([{ transform: "scale(0.3)", opacity: 0 }, { transform: "scale(1.18)", opacity: 1, offset: 0.6 }, { transform: "scale(1)", opacity: 1 }], { duration: RISE_MS, easing: "cubic-bezier(.2,.9,.3,1.2)" });
    }
  }
  onRefresh(panel, () => { if (!busy) render(); });
  render();
}

